import { useCallback, useEffect, useState } from "react";
import type { HLJSApi, HighlightResult } from "highlight.js";
import { useThemeContext } from "@circle-react/providers";

export const useHighlightCode = () => {
  const { isDarkAppearance } = useThemeContext();
  const [highlighter, setHighlighter] = useState<HLJSApi | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const loadHighlighter = async () => {
      try {
        const { default: hljs } = await import(
          /* webpackChunkName: "HighlightJs_Core" */ "highlight.js/lib/core"
        );

        const { default: xml } = await import(
          /* webpackChunkName: "HighlightJs_Languages_Html" */ "highlight.js/lib/languages/xml"
        );
        hljs.registerLanguage("xml", xml);

        const { default: javascript } = await import(
          /* webpackChunkName: "HighlightJs_Languages_JavaScript" */ "highlight.js/lib/languages/javascript"
        );
        hljs.registerLanguage("javascript", javascript);

        const { default: css } = await import(
          /* webpackChunkName: "HighlightJs_Languages_CSS" */ "highlight.js/lib/languages/css"
        );
        hljs.registerLanguage("css", css);

        hljs.configure({ languages: ["xml", "javascript", "css"] });
        setHighlighter(hljs);
        setIsLoaded(true);
      } catch (error: unknown) {
        console.error("Error loading syntax highlighter:", error);
      }
    };

    void loadHighlighter();
  }, []);

  useEffect(() => {
    if (isLoaded && highlighter) {
      highlighter.highlightAll();
    }
  }, [isDarkAppearance, isLoaded, highlighter]);

  const highlightCode = useCallback(
    (code: string) => {
      if (!highlighter || !isLoaded) {
        return code;
      }

      try {
        const result: HighlightResult = highlighter.highlight(code, {
          language: "xml",
        });
        return result.value;
      } catch (error: unknown) {
        console.error("Error highlighting HTML code:", error);
        return code;
      }
    },
    [highlighter, isLoaded],
  );

  return { highlightCode };
};
